'use client';

import { useMemo, useState } from 'react';

interface PricingSummaryProps {
    // Base pricing
    baseMonthlyPrice: number;
    isYearly: boolean;
    currency: string;

    // Trial info
    trialDays: number;
    originalTrialDays?: number | null; // Backup of trial days before coupon extends them

    // Annual discount (e.g., 50%)
    annualDiscountPct?: number;

    // Coupon applied
    couponApplied: boolean;
    couponReason?: string;
    priceCoupon?: number | null; // Monthly price after coupon
    priceCouponFull?: number | null; // Total price after coupon
    trialExtendedViaCoupon?: boolean;
    extendedTrialDays?: number | null;

    // Theme
    isDarkMode?: boolean;
}

/**
 * Displays detailed pricing breakdown with discounts
 * Matches the web app's trustworthy pricing display
 */
export function PricingSummary({
    baseMonthlyPrice,
    isYearly,
    currency,
    trialDays,
    originalTrialDays,
    annualDiscountPct = 50,
    couponApplied,
    couponReason,
    priceCoupon,
    priceCouponFull,
    trialExtendedViaCoupon,
    extendedTrialDays,
    isDarkMode = true,
}: PricingSummaryProps) {
    // Calculate all pricing values
    const pricing = useMemo(() => {
        const baseAnnualPrice = baseMonthlyPrice * 12;
        const annualTotalPrice =
            baseAnnualPrice * (1 - annualDiscountPct / 100);
        const annualMonthlyPrice = annualTotalPrice / 12;

        // Final price after coupon (if applied)
        let finalMonthlyPrice = isYearly
            ? annualMonthlyPrice
            : baseMonthlyPrice;
        let finalTotalPrice = isYearly ? annualTotalPrice : baseMonthlyPrice;

        if (
            couponApplied &&
            priceCoupon !== null &&
            priceCoupon !== undefined
        ) {
            finalMonthlyPrice = priceCoupon;
        }
        if (
            couponApplied &&
            priceCouponFull !== null &&
            priceCouponFull !== undefined
        ) {
            finalTotalPrice = priceCouponFull;
        }

        // Effective trial days
        const effectiveTrialDays =
            trialExtendedViaCoupon && extendedTrialDays
                ? extendedTrialDays
                : trialDays;

        // Trial end date
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + effectiveTrialDays);

        // Check if coupon actually changes the price (not just floating-point rounding)
        // A coupon might only extend trial without changing price
        const priceBeforeCoupon = isYearly
            ? annualMonthlyPrice
            : baseMonthlyPrice;
        const couponChangesPriceMeaningfully =
            couponApplied &&
            priceCoupon !== null &&
            priceCoupon !== undefined &&
            Math.abs(priceBeforeCoupon - priceCoupon) > 0.02; // Tolerance for rounding ($0.01 diff is not meaningful)

        return {
            baseMonthlyPrice,
            baseAnnualPrice,
            annualTotalPrice,
            annualMonthlyPrice,
            finalMonthlyPrice,
            finalTotalPrice,
            effectiveTrialDays,
            trialEndDate,
            couponChangesPriceMeaningfully,
        };
    }, [
        baseMonthlyPrice,
        isYearly,
        annualDiscountPct,
        couponApplied,
        priceCoupon,
        priceCouponFull,
        trialDays,
        trialExtendedViaCoupon,
        extendedTrialDays,
    ]);

    const formatPrice = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Short date format for headers (e.g., "Dec 29")
    const formatShortDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    };

    const isFreeWithCoupon = pricing.finalTotalPrice === 0;

    // When there's a trial, use the two-section layout (TODAY / AFTER TRIAL)
    // When no trial, use the original single-section layout
    const hasTrial = pricing.effectiveTrialDays > 0;

    // Collapsible pricing details when ANY discount exists (annual OR coupon)
    // This keeps focus on $0 today while transparency is one tap away
    const hasAnyDiscount = isYearly || pricing.couponChangesPriceMeaningfully;
    const [isPricingExpanded, setIsPricingExpanded] = useState(false);

    return (
        <div className="space-y-3">
            {hasTrial ? (
                <>
                    {/* ===== TODAY SECTION - Purple (dark) / Green (light) ===== */}
                    <div
                        className={`rounded-lg p-4 space-y-2 ${
                            isDarkMode
                                ? 'bg-purple-900/20 border border-purple-800'
                                : 'bg-green-50 border border-green-200'
                        }`}
                    >
                        <p
                            className={`text-xs font-semibold uppercase tracking-wide ${
                                isDarkMode
                                    ? 'text-purple-400'
                                    : 'text-green-700'
                            }`}
                        >
                            Today
                        </p>
                        {/* Due Today Row */}
                        <div className="flex justify-between items-center">
                            <span
                                className={`font-medium flex items-center gap-2 ${
                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}
                            >
                                <svg
                                    className={`w-5 h-5 ${
                                        isDarkMode
                                            ? 'text-purple-400'
                                            : 'text-green-600'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                Due today
                            </span>
                            <span
                                className={`text-2xl font-bold ${
                                    isDarkMode
                                        ? 'text-purple-400'
                                        : 'text-green-600'
                                }`}
                            >
                                {formatPrice(0)}
                            </span>
                        </div>
                        {/* Free Trial Row */}
                        <div className="flex justify-between items-center text-sm">
                            <span
                                className={`ml-7 ${
                                    isDarkMode
                                        ? 'text-gray-400'
                                        : 'text-gray-600'
                                }`}
                            >
                                Free trial
                            </span>
                            <span
                                className={`font-medium ${
                                    isDarkMode
                                        ? 'text-purple-400'
                                        : 'text-green-600'
                                }`}
                            >
                                {trialExtendedViaCoupon && extendedTrialDays ? (
                                    <>
                                        <span
                                            className={`line-through mr-2 ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {originalTrialDays ?? trialDays}{' '}
                                            days
                                        </span>
                                        {extendedTrialDays} days
                                    </>
                                ) : (
                                    `${trialDays} days`
                                )}
                            </span>
                        </div>
                        {/* Reminder Row */}
                        <div
                            className={`flex items-center gap-2 text-sm ml-7 pt-1 ${
                                isDarkMode
                                    ? 'text-purple-400'
                                    : 'text-green-700'
                            }`}
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                />
                            </svg>
                            <span>We'll remind you 3 days before</span>
                        </div>
                    </div>

                    {/* ===== AFTER YOUR TRIAL SECTION - Gray, de-emphasized ===== */}
                    {!isFreeWithCoupon && (
                        <div
                            className={`rounded-lg overflow-hidden ${
                                isDarkMode
                                    ? 'bg-gray-800 border border-gray-700'
                                    : 'bg-gray-50 border border-gray-200'
                            }`}
                        >
                            {/* Collapsible header when any discount exists */}
                            {hasAnyDiscount ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setIsPricingExpanded(!isPricingExpanded)
                                    }
                                    className={`w-full p-4 text-left transition-colors ${
                                        isDarkMode
                                            ? 'hover:bg-gray-750'
                                            : 'hover:bg-gray-100'
                                    }`}
                                >
                                    {/* Top row: Title and View details */}
                                    <div className="flex justify-between items-center">
                                        <p
                                            className={`text-xs font-medium uppercase tracking-wide ${
                                                isDarkMode
                                                    ? 'text-gray-400'
                                                    : 'text-gray-500'
                                            }`}
                                        >
                                            After your trial (
                                            {formatShortDate(
                                                pricing.trialEndDate
                                            )}
                                            )
                                        </p>
                                        <div
                                            className={`flex items-center gap-1 text-xs ${
                                                isDarkMode
                                                    ? 'text-gray-400'
                                                    : 'text-gray-500'
                                            }`}
                                        >
                                            <span>
                                                {isPricingExpanded
                                                    ? 'Hide'
                                                    : 'View'}{' '}
                                                details
                                            </span>
                                            <svg
                                                className={`w-4 h-4 transition-transform duration-200 ${
                                                    isPricingExpanded
                                                        ? 'rotate-180'
                                                        : ''
                                                }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    {/* Bottom row: Price (shown when collapsed) */}
                                    {!isPricingExpanded && (
                                        <div className="flex justify-between items-center mt-2">
                                            <span
                                                className={`text-sm ${
                                                    isDarkMode
                                                        ? 'text-gray-400'
                                                        : 'text-gray-500'
                                                }`}
                                            >
                                                Billed{' '}
                                                {isYearly
                                                    ? 'annually'
                                                    : 'monthly'}
                                            </span>
                                            <span
                                                className={`text-base font-semibold ${
                                                    isDarkMode
                                                        ? 'text-gray-300'
                                                        : 'text-gray-700'
                                                }`}
                                            >
                                                <span
                                                    className={`line-through mr-2 text-sm font-normal ${
                                                        isDarkMode
                                                            ? 'text-gray-500'
                                                            : 'text-gray-400'
                                                    }`}
                                                >
                                                    {formatPrice(
                                                        isYearly
                                                            ? pricing.baseAnnualPrice
                                                            : pricing.baseMonthlyPrice
                                                    )}
                                                </span>
                                                {formatPrice(
                                                    pricing.finalTotalPrice
                                                )}
                                                {isYearly ? '/yr' : '/mo'}
                                            </span>
                                        </div>
                                    )}
                                </button>
                            ) : (
                                <div className="p-4 pb-2">
                                    <p
                                        className={`text-xs font-medium uppercase tracking-wide ${
                                            isDarkMode
                                                ? 'text-gray-400'
                                                : 'text-gray-500'
                                        }`}
                                    >
                                        After your trial (
                                        {formatShortDate(pricing.trialEndDate)})
                                    </p>
                                </div>
                            )}

                            {/* Collapsible content */}
                            <div
                                className={`transition-all duration-200 ease-in-out ${
                                    hasAnyDiscount && !isPricingExpanded
                                        ? 'max-h-0 opacity-0 overflow-hidden'
                                        : 'max-h-96 opacity-100'
                                }`}
                            >
                                <div className="space-y-2 px-4 pb-4">
                                    {/* Annual Discount Row (yearly plans only) */}
                                    {isYearly && (
                                        <div className="flex justify-between items-center text-sm">
                                            <span
                                                className={
                                                    isDarkMode
                                                        ? 'text-gray-400'
                                                        : 'text-gray-500'
                                                }
                                            >
                                                {annualDiscountPct}% annual
                                                discount
                                            </span>
                                            <div className="text-right">
                                                <span
                                                    className={`line-through mr-2 ${
                                                        isDarkMode
                                                            ? 'text-gray-500'
                                                            : 'text-gray-400'
                                                    }`}
                                                >
                                                    {formatPrice(
                                                        pricing.baseMonthlyPrice
                                                    )}
                                                </span>
                                                <span
                                                    className={
                                                        isDarkMode
                                                            ? 'text-gray-300'
                                                            : 'text-gray-700'
                                                    }
                                                >
                                                    {formatPrice(
                                                        pricing.annualMonthlyPrice
                                                    )}
                                                    /mo
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Coupon Discount Row - only show if coupon actually changes price */}
                                    {pricing.couponChangesPriceMeaningfully &&
                                        couponReason && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span
                                                    className={
                                                        isDarkMode
                                                            ? 'text-purple-400'
                                                            : 'text-purple-600'
                                                    }
                                                >
                                                    {couponReason}
                                                </span>
                                                <div className="text-right">
                                                    <span
                                                        className={`line-through mr-2 ${
                                                            isDarkMode
                                                                ? 'text-gray-500'
                                                                : 'text-gray-400'
                                                        }`}
                                                    >
                                                        {formatPrice(
                                                            isYearly
                                                                ? pricing.annualMonthlyPrice
                                                                : pricing.baseMonthlyPrice
                                                        )}
                                                    </span>
                                                    <span
                                                        className={`font-medium ${
                                                            isDarkMode
                                                                ? 'text-purple-400'
                                                                : 'text-purple-600'
                                                        }`}
                                                    >
                                                        {formatPrice(
                                                            pricing.finalMonthlyPrice
                                                        )}
                                                        /mo
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                    {/* Total/Billed Row */}
                                    <div
                                        className={`flex justify-between items-center pt-2 border-t ${
                                            isDarkMode
                                                ? 'border-gray-600'
                                                : 'border-gray-200'
                                        }`}
                                    >
                                        <span
                                            className={`text-sm ${
                                                isDarkMode
                                                    ? 'text-gray-400'
                                                    : 'text-gray-500'
                                            }`}
                                        >
                                            {isYearly
                                                ? 'Billed annually'
                                                : 'Billed monthly'}
                                        </span>
                                        <div className="text-right">
                                            {/* Show strikethrough original price when there's a discount */}
                                            {hasAnyDiscount && (
                                                <span
                                                    className={`text-sm line-through mr-2 ${
                                                        isDarkMode
                                                            ? 'text-gray-500'
                                                            : 'text-gray-400'
                                                    }`}
                                                >
                                                    {formatPrice(
                                                        isYearly
                                                            ? pricing.baseAnnualPrice
                                                            : pricing.baseMonthlyPrice
                                                    )}
                                                    {isYearly ? '/yr' : '/mo'}
                                                </span>
                                            )}
                                            <span
                                                className={`text-lg font-bold ${
                                                    isDarkMode
                                                        ? 'text-gray-300'
                                                        : 'text-gray-700'
                                                }`}
                                            >
                                                {formatPrice(
                                                    pricing.finalTotalPrice
                                                )}
                                                {isYearly ? '/yr' : '/mo'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <>
                    {/* ===== NO TRIAL - Original single-section layout ===== */}
                    <div
                        className={`rounded-lg p-4 space-y-3 ${
                            isDarkMode
                                ? 'bg-gray-800 border border-gray-700'
                                : 'bg-gray-50'
                        }`}
                    >
                        {/* Annual Discount Row (yearly plans only) */}
                        {isYearly && (
                            <div className="flex justify-between items-center text-sm">
                                <span
                                    className={
                                        isDarkMode
                                            ? 'text-gray-400'
                                            : 'text-gray-600'
                                    }
                                >
                                    {annualDiscountPct}% annual discount
                                </span>
                                <div className="text-right">
                                    <span
                                        className={`line-through mr-2 ${
                                            isDarkMode
                                                ? 'text-gray-500'
                                                : 'text-gray-400'
                                        }`}
                                    >
                                        {formatPrice(pricing.baseMonthlyPrice)}
                                    </span>
                                    <span
                                        className={
                                            isDarkMode
                                                ? 'text-white'
                                                : 'text-gray-900'
                                        }
                                    >
                                        {formatPrice(
                                            pricing.annualMonthlyPrice
                                        )}
                                        /mo
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Coupon Discount Row - only show if coupon actually changes price */}
                        {pricing.couponChangesPriceMeaningfully &&
                            couponReason && (
                                <div className="flex justify-between items-center text-sm">
                                    <span
                                        className={
                                            isDarkMode
                                                ? 'text-purple-400'
                                                : 'text-purple-600'
                                        }
                                    >
                                        {couponReason}
                                    </span>
                                    <div className="text-right">
                                        <span
                                            className={`line-through mr-2 ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {formatPrice(
                                                isYearly
                                                    ? pricing.annualMonthlyPrice
                                                    : pricing.baseMonthlyPrice
                                            )}
                                        </span>
                                        <span
                                            className={`font-medium ${
                                                isDarkMode
                                                    ? 'text-purple-400'
                                                    : 'text-purple-600'
                                            }`}
                                        >
                                            {isFreeWithCoupon
                                                ? 'Free'
                                                : `${formatPrice(
                                                      pricing.finalMonthlyPrice
                                                  )}/mo`}
                                        </span>
                                    </div>
                                </div>
                            )}

                        {/* Divider */}
                        <div
                            className={`border-t pt-3 ${
                                isDarkMode
                                    ? 'border-gray-600'
                                    : 'border-gray-200'
                            }`}
                        >
                            {/* Final Price */}
                            <div className="flex justify-between items-center">
                                <span
                                    className={`font-medium ${
                                        isDarkMode
                                            ? 'text-white'
                                            : 'text-gray-900'
                                    }`}
                                >
                                    {isFreeWithCoupon ? 'Total' : 'Price'}
                                </span>
                                <span
                                    className={`text-2xl font-bold ${
                                        isDarkMode
                                            ? 'text-white'
                                            : 'text-gray-900'
                                    }`}
                                >
                                    {isFreeWithCoupon
                                        ? 'Free'
                                        : `${formatPrice(
                                              pricing.finalMonthlyPrice
                                          )}/mo`}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Total Section (no trial) */}
                    {!isFreeWithCoupon && (
                        <div
                            className={`rounded-lg p-4 ${
                                isDarkMode
                                    ? 'bg-purple-900/20 border border-purple-800'
                                    : 'bg-purple-50'
                            }`}
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <p
                                        className={`text-sm ${
                                            isDarkMode
                                                ? 'text-gray-300'
                                                : 'text-gray-600'
                                        }`}
                                    >
                                        Total{' '}
                                        <span
                                            className={
                                                isDarkMode
                                                    ? 'text-gray-400'
                                                    : 'text-gray-500'
                                            }
                                        >
                                            (
                                            {isYearly
                                                ? 'Billed annually'
                                                : 'Billed monthly'}
                                            )
                                        </span>
                                    </p>
                                    {isYearly && !couponApplied && (
                                        <p
                                            className={`text-xs line-through ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {formatPrice(
                                                pricing.baseAnnualPrice
                                            )}
                                        </p>
                                    )}
                                    {isYearly && couponApplied && (
                                        <p
                                            className={`text-xs line-through ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {formatPrice(
                                                pricing.annualTotalPrice
                                            )}
                                        </p>
                                    )}
                                </div>
                                <span
                                    className={`text-xl font-bold ${
                                        isDarkMode
                                            ? 'text-purple-400'
                                            : 'text-purple-700'
                                    }`}
                                >
                                    {formatPrice(pricing.finalTotalPrice)}
                                    {isYearly ? '/yr' : '/mo'}
                                </span>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Trial Info Footer - only show if there's a trial */}
            {hasTrial && (
                <div
                    className={`text-xs text-center px-2 space-y-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                >
                    <p>
                        Your trial period ends on{' '}
                        <span className="font-medium">
                            {formatDate(pricing.trialEndDate)}
                        </span>{' '}
                        after which your card will be charged. However, you will
                        not be charged if you cancel before the trial period.
                    </p>
                </div>
            )}

            {/* Social Proof */}
            <div
                className={`text-center pt-2 border-t ${
                    isDarkMode ? 'border-gray-700' : 'border-gray-100'
                }`}
            >
                <p
                    className={`text-sm font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                >
                    Join thousands of art lovers streaming with Niio
                </p>
                <p
                    className={`text-xs mt-0.5 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                >
                    Trusted by Hilton Hotels, Samsung &bull; Award-winning
                    platform
                </p>
            </div>
        </div>
    );
}

export default PricingSummary;
